// Entrada num módulo pela visão de UM cliente (nav 2026): o miolo que a
// sidebar do cliente (navegacao-v2-sidebar.vue) e a busca global
// (global-search.vue) compartilham — resolver do módulo → navegar → aguardar
// o host do modal → emitir. Quem chama injeta `go` (sua forma de navegar) e
// `isStale` (sua forma de saber se uma seleção mais recente já começou).
import { GlobalBus } from '@services/bus'
import { shouldOpenClienteModal } from '@modules/navegacao-v2/utils/cliente-modal'
import { waitClienteModalHostReady } from '@modules/navegacao-v2/utils/cliente-modal-host'
import { resolveModuleEntranceCliente } from '@modules/navegacao-v2/utils/module-entrance'

const NEVER_STALE = () => false

/**
 * Executa o resultado do resolver do módulo: navega pro destino resolvido
 * (ex.: crm.relationship vs crm.leads) e SÓ DEPOIS espera o host do modal
 * DAQUELE módulo ficar de fato montado (waitClienteModalHostReady, keyed por
 * moduleKey — cada módulo tem seu próprio host) antes de emitir.
 * router.push() e $nextTick() NÃO bastam aqui: a view de destino (ex.:
 * crm-nav-bar.vue, via lazyLoadView) é um async component ANINHADO com
 * loading/delay/error próprios (@utils/LazyLoad.js), resolvido de forma
 * desacoplada da navegação — emitir cedo demais faz o evento cair no vazio
 * (nenhum host montado ainda) ou pegar o host antigo (do shell) no instante em
 * que está sendo destruído pela troca de contexto (dialog abre e some no mesmo
 * tick, só o scrim/overlay fica órfão) — os dois bugs já observados.
 *
 * @param {{routeName?: string, params?: Object, modal?: {name: string, payload: *}}} resolved
 * @param {Object} opts
 * @param {string} opts.moduleKey chave do host do modal (item.clienteEntranceModule)
 * @param {Function} opts.go (routeName, params?) => Promise — navegação de quem chama
 * @param {Function} [opts.isStale] () => boolean — true se uma seleção mais
 *   recente começou; nesse caso o modal desta NÃO é emitido
 */
export async function applyClienteEntrance(
  { routeName, params, modal } = {},
  { moduleKey, go, isStale = NEVER_STALE } = {}
) {
  const hasModal = !!(modal && modal.name)
  if (routeName) {
    // Só repassa params quando o resolver de fato os declara (ex: Wealth
    // Service) — chamar go(routeName, undefined) sempre mudaria a
    // assinatura observada em go() pros módulos que não usam params.
    await (params ? go(routeName, params) : go(routeName))
    // Só espera o host do modal se houver modal pra abrir — módulos como
    // o Wealth Service resolvem só a rota (ver ws/utils/cliente-entrance.js),
    // sem host nenhum registrado; esperar aqui travaria pelo timeout inteiro
    // (waitClienteModalHostReady) sem nenhum ganho.
    if (hasModal) {
      const ready = await waitClienteModalHostReady(moduleKey)
      // Bug real observado: clicar num item client-centric (ex.: CRM ou
      // Compliance) e, ANTES do modal abrir, já trocar de módulo — o
      // clique novo torna esta seleção obsoleta enquanto este await ainda
      // está pendente. Sem esta checagem, o emit abaixo dispara de qualquer
      // jeito: ou pro vazio (host do módulo antigo nunca ficou pronto —
      // timeout, ready=false) ou pro host antigo bem no instante em que está
      // sendo destruído pela troca de contexto — nos dois casos sobra um
      // <BaseModal> tentando abrir num host que já não existe, e o
      // scrim/overlay fica travado na tela. Só emite se o host respondeu a
      // tempo E nenhuma seleção mais recente começou nesse meio tempo.
      if (!ready || isStale()) return
    }
  }
  if (hasModal) {
    if (isStale()) return
    GlobalBus.$emit(modal.name, modal.payload)
  }
}

/**
 * Entrada completa num item de CLIENTE_GROUPS: se o item abre modal focado no
 * cliente (shouldOpenClienteModal) e o resolver do módulo devolve uma entrada,
 * aplica-a; senão navega para `fallbackRouteName` (quem chama resolve via
 * resolveDynamicRoute, que usa o clienteSkipTo).
 *
 * @param {Object} opts
 * @param {Object} opts.item item de CLIENTE_GROUPS já filtrado por visibilidade
 * @param {Object} opts.client cliente ativo ($currentClient())
 * @param {Function} opts.go (routeName, params?) => Promise
 * @param {Function} [opts.isStale] () => boolean
 * @param {string} opts.fallbackRouteName destino sem resolver/entrada
 */
export async function enterClienteModule({
  item,
  client,
  go,
  isStale = NEVER_STALE,
  fallbackRouteName,
} = {}) {
  const opensModal = shouldOpenClienteModal(item, { isCliente: true, client })
  const resolved = opensModal
    ? await resolveModuleEntranceCliente(item.clienteEntranceModule, {
        client,
        item,
      })
    : null
  if (resolved) {
    await applyClienteEntrance(resolved, {
      moduleKey: item.clienteEntranceModule,
      go,
      isStale,
    })
    return
  }
  if (isStale()) return
  await go(fallbackRouteName)
}
